/* eslint-disable i18next/no-literal-string */
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Box, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
    getRouteForbidden,
    getRouteReportsSavingsSummary,
    getRouteReportsSavingsSummaryCategory,
    getRouteReportsSavingsSummaryProduct,
} from 'shared/const/router';
import { formatReportCurrency } from 'shared/lib/reportFormatters';
import { ReportDrillInHeader } from 'shared/ui/ReportDrillInHeader';
import { ReportMetric } from 'shared/ui/ReportMetric/ReportMetric';
import { Page } from 'widgets/Page';
import { SavingsSummaryCategoriesSection } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryCategoriesSection';
import { SavingsSummaryProductDetailsDrawer } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductDetailsDrawer';
import { SavingsSummaryProductsSection } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductsSection';
import { SavingsSummaryTotalSavingsMetric } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryTotalSavingsMetric';

import { useReportsPageSavingsSummaryProperty } from '../model/hooks/useReportsPageSavingsSummaryProperty';

import type { ProductMetricsDto } from 'entities/ReportSavingsSummary';
import type { ReportWidgetInteractionPayload } from 'widgets/ComposableReportWidget';

const ReportsPageSavingsSummaryProperty = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedProductSku, setSelectedProductSku] = useState<string | null>(
        null,
    );
    const {
        isSavingsSummaryVisible,
        propertyId,
        propertyName,
        loading,
        summary,
        byCategory,
        products,
        totalSaved,
        savingsPerUnit,
        savingsPerUnitPerMonth,
        orderCount,
        savingsPerOrder,
        dateFrom,
        dateTo,
        datePreset,
        lastRequestParamsPresent,
    } = useReportsPageSavingsSummaryProperty();

    const handleBack = useCallback(() => {
        navigate(getRouteReportsSavingsSummary(), {
            state: { fromDrillIn: true },
        });
    }, [navigate]);

    const handleProductRowClick = useCallback(
        (sku: string, product: ProductMetricsDto) => {
            navigate(getRouteReportsSavingsSummaryProduct(sku), {
                state: {
                    product,
                    fromMissedSavings: false,
                },
            });
        },
        [navigate],
    );

    const handleCategorySelect = useCallback(
        (payload: ReportWidgetInteractionPayload) => {
            const meta = payload.item.meta as {
                categoryId?: string;
                categoryTitle?: string;
            };
            const categoryId = meta.categoryId;

            if (!categoryId) {
                return;
            }

            navigate(getRouteReportsSavingsSummaryCategory(categoryId), {
                state: {
                    categoryTitle: meta.categoryTitle ?? payload.item.label,
                    contextTotalSavings: summary?.savingsAmount,
                },
            });
        },
        [navigate, summary?.savingsAmount],
    );

    const openProductDetails = useCallback((sku: string) => {
        setSelectedProductSku(sku);
    }, []);

    const closeProductDetails = useCallback(() => {
        setSelectedProductSku(null);
    }, []);

    if (isSavingsSummaryVisible === undefined) {
        return null;
    }

    if (!isSavingsSummaryVisible) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    if (!propertyId || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSavingsSummary()} replace />;
    }

    return (
        <Page
            dataTestId="reports-page-savings-summary-property"
            padding="24px 35px"
        >
            <Box>
                <ReportDrillInHeader
                    title={propertyName}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    datePreset={datePreset}
                    onBack={handleBack}
                    backButtonTitle={t('Savings Summary')}
                    backButtonId="back-to-savings-summary-button"
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        marginBottom: 3,
                    }}
                >
                    <SavingsSummaryTotalSavingsMetric
                        summary={summary ?? null}
                        primaryAmount={totalSaved}
                        loading={loading}
                    />
                    <ReportMetric
                        title={t('savingsSummary.savingsPerUnit')}
                        icon={<AttachMoneyIcon fontSize="small" />}
                        primary={
                            savingsPerUnit == null
                                ? '—'
                                : formatReportCurrency(savingsPerUnit)
                        }
                        primaryColor="#16a34a"
                        secondary={
                            savingsPerUnitPerMonth === null
                                ? '—'
                                : `${formatReportCurrency(
                                      savingsPerUnitPerMonth,
                                  )} ${t(
                                      'savingsSummary.perUnitPerMonthSuffix',
                                  )}`
                        }
                        loading={loading}
                    />
                    <ReportMetric
                        title={t('Orders')}
                        icon={<ShoppingCartIcon fontSize="small" />}
                        primary={orderCount.toLocaleString()}
                        loading={loading}
                    />
                    <ReportMetric
                        title={t(
                            'savingsSummary.propertyDrillInSavingsPerOrder',
                        )}
                        icon={<AttachMoneyIcon fontSize="small" />}
                        primary={
                            savingsPerOrder == null
                                ? '—'
                                : formatReportCurrency(savingsPerOrder)
                        }
                        primaryColor="#16a34a"
                        loading={loading}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '24px',
                            marginBottom: '40px',
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" color="text.secondary">
                                By Supplier TBD
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <SavingsSummaryCategoriesSection
                                byCategory={byCategory}
                                loading={loading}
                                onCategorySelect={handleCategorySelect}
                                tableOnly
                                tableColumnIds={[
                                    'category',
                                    'savings',
                                    'totalSpend',
                                ]}
                                showBottomBorder={false}
                            />
                        </Box>
                    </Box>
                    <SavingsSummaryProductsSection
                        products={products}
                        totalSavingsAmount={summary?.savingsAmount}
                        loading={loading}
                        onProductDetailsClick={openProductDetails}
                        onOpenProductDrillIn={handleProductRowClick}
                        tableOnly
                        tableColumnIds={[
                            'item',
                            'brand',
                            'sku',
                            'properties',
                            'quantity',
                            'avgSupplierPrice',
                            'pricePaid',
                            'totalSaved',
                            'percentSaved',
                        ]}
                        subheader={t(
                            'savingsSummary.productsSubheaderAtProperty',
                            {
                                propertyNames: propertyName,
                            },
                        )}
                        showBottomBorder={false}
                    />
                    <SavingsSummaryProductDetailsDrawer
                        sku={selectedProductSku}
                        open={Boolean(selectedProductSku)}
                        onClose={closeProductDetails}
                    />
                </Box>
            </Box>
        </Page>
    );
};

export default ReportsPageSavingsSummaryProperty;
